import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Magnetic, EASE } from "./ui/motion";
import { journey, subscribeJourney } from "../journey/journeyState";
import { SECTIONS } from "../journey/sections";
import { scrollToSection } from "../journey/Journey";

const LINKS = [
  { id: "about", label: "About" },
  { id: "skills", label: "Skills" },
  { id: "experience", label: "Experience" },
  { id: "projects", label: "Projects" },
  { id: "contact", label: "Contact" },
];

export default function Navbar({ ready }) {
  const [active, setActive] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    return subscribeJourney(() => {
      const id = SECTIONS[journey.activeIndex]?.id ?? "";
      // the outro panel still belongs to Contact in the nav
      setActive(id === "outro" ? "contact" : id);
    });
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <>
      <motion.header
        className="fixed top-0 inset-x-0 z-50 bg-gradient-to-b from-ink via-ink/80 to-transparent"
        initial={{ y: -80, opacity: 0 }}
        animate={ready ? { y: 0, opacity: 1 } : {}}
        transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
      >
        <nav className="flex items-center justify-between px-6 sm:px-10 py-5">
          {/* wordmark */}
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="font-display text-xl tracking-tight text-paper"
          >
            TN<span className="text-acid">.</span>
          </button>

          {/* desktop links */}
          <ul className="hidden mdl:flex items-center gap-1">
            {LINKS.map((l) => (
              <li key={l.id}>
                <Magnetic strength={0.4}>
                  <button
                    onClick={() => go(l.id)}
                    className="group relative px-4 py-2 font-mono text-[12px] uppercase tracking-[0.2em] text-paper"
                  >
                    <span
                      className={`mr-1.5 transition-colors ${
                        active === l.id ? "text-acid" : "text-paper-dim"
                      }`}
                    >
                      {active === l.id ? "●" : "○"}
                    </span>
                    {l.label}
                  </button>
                </Magnetic>
              </li>
            ))}
          </ul>

          {/* mobile toggle */}
          <button
            onClick={() => setOpen((o) => !o)}
            className="mdl:hidden flex flex-col gap-1.5 w-8 items-end"
            aria-label="Toggle menu"
          >
            <motion.span
              className="h-px bg-paper block"
              animate={open ? { width: 28, rotate: 45, y: 6 } : { width: 28, rotate: 0, y: 0 }}
            />
            <motion.span
              className="h-px bg-paper block"
              animate={open ? { width: 28, rotate: -45, y: -6 } : { width: 20, rotate: 0, y: 0 }}
            />
          </button>
        </nav>
      </motion.header>

      {/* mobile overlay */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-40 bg-ink-soft flex flex-col justify-center px-8 mdl:hidden"
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            exit={{ clipPath: "inset(0 0 100% 0)" }}
            transition={{ duration: 0.7, ease: EASE }}
          >
            <ul className="flex flex-col gap-2">
              {LINKS.map((l, i) => (
                <li key={l.id} className="overflow-hidden">
                  <motion.button
                    onClick={() => go(l.id)}
                    className="display text-6xl text-paper hover:text-acid transition-colors flex items-baseline gap-4"
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ delay: 0.15 + i * 0.07, duration: 0.7, ease: EASE }}
                  >
                    <span className="font-mono text-xs text-acid">0{i + 1}</span>
                    {l.label}
                  </motion.button>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
